import type { SliderProps } from '@mui/material/Slider'
import Slider from '@mui/material/Slider'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { Box } from '../../components/Box.tsx'
import { IconButton } from '../../components/IconButton.tsx'
import { ids } from '../../ids.ts'

export const SignalStrengthSlider = <TInfiniteOption extends boolean>({
	min,
	max,
	infiniteOption,
	range: inputRange,
	onChange,
	onChangeCommited,
	additionalMarks,
	disabled,
	dataTa,
}: {
	min: number
	max: number
	infiniteOption: TInfiniteOption
	range: SignalStrengthRangeSelection<TInfiniteOption>
	onChange: (range: SignalStrengthRangeSelection<TInfiniteOption>) => void
	onChangeCommited: (range: SignalStrengthRangeSelection<TInfiniteOption>) => void
	additionalMarks?: { label: string; value: number }[]
	disabled?: boolean
	dataTa?: string
}) => {
	const { t } = useTranslation()
	const [sliderRef, setSliderRef] = useState<HTMLDivElement | null>(null)
	const [minDistanceForMarkToBeVisible, setMinDistanceForMarkToBeVisible] = useState<number>(9999)

	const sliderMin = infiniteOption ? min - 1 : min
	const sliderMax = infiniteOption ? max + 1 : max

	const isInfinite = (value: number) => infiniteOption && (value === sliderMin || value === sliderMax)
	const range: { min: number; max: number } = (() => {
		const min = inputRange.min ?? sliderMin
		const max = inputRange.max ?? sliderMax
		return { min, max }
	})()

	const invertedRange = inputRange.inverted

	const marks: Writable<SliderProps['marks']> = [
		{ label: infiniteOption ? '-∞' : `${sliderMin} kA`, value: sliderMin },
		{ label: infiniteOption ? '∞' : `${sliderMax} kA`, value: sliderMax },
		...(additionalMarks ?? []),
	].map((mark, index, arr) => {
		const isTooCloseToHandles =
			Math.abs(mark.value - range.min) < minDistanceForMarkToBeVisible || Math.abs(mark.value - range.max) < minDistanceForMarkToBeVisible
		const isTooCloseToPreviousMarks = arr
			.slice(0, index)
			.some((otherMark) => Math.abs(mark.value - otherMark.value) < minDistanceForMarkToBeVisible)

		return isTooCloseToHandles || isTooCloseToPreviousMarks ? { ...mark, label: ' ' } : mark
	})

	if (Math.abs(range.min - range.max) >= minDistanceForMarkToBeVisible) {
		marks.push(
			{ label: isInfinite(range.min) ? '-∞' : `${range.min} kA`, value: range.min },
			{ label: isInfinite(range.max) ? '∞' : `${range.max} kA`, value: range.max },
		)
	}

	useEffect(() => {
		if (!sliderRef) return

		const updateMinDistance = () => {
			const sliderWidth = sliderRef.scrollWidth
			const sliderRange = max - min + (infiniteOption ? 2 : 0)

			setMinDistanceForMarkToBeVisible((sliderRange / sliderWidth) * 60)
		}

		const resizeObserver = new ResizeObserver(updateMinDistance)
		updateMinDistance()

		resizeObserver.observe(sliderRef)
		return () => resizeObserver.disconnect()
	}, [sliderRef, max, min, infiniteOption])

	return (
		<Box pt={1} px={1} flex data-ta={dataTa}>
			<Box ref={setSliderRef} flex width={'100%'}>
				<Slider
					data-ta={ids.signalStrengthSlider.slider}
					sx={{ mr: 4 }}
					// The Slider design currently requires mark labels to be at bottom, should be revisited
					slotProps={{ markLabel: { style: { top: 22, bottom: 'unset' } } }}
					disabled={disabled}
					min={sliderMin}
					max={sliderMax}
					value={[range.min, range.max]}
					onChange={(_, newRange) => {
						const newMin = newRange[0] ?? sliderMin
						const newMax = newRange[1] ?? sliderMax

						if (newMin !== newMax) {
							return onChange({ ...inputRange, min: isInfinite(newMin) ? undefined : newMin, max: isInfinite(newMax) ? undefined : newMax })
						}
					}}
					onChangeCommitted={(_, newRange) => {
						const newMin = newRange[0] ?? sliderMin
						const newMax = newRange[1] ?? sliderMax

						if (newMin !== newMax) {
							return onChangeCommited({
								...inputRange,
								min: isInfinite(newMin) ? undefined : newMin,
								max: isInfinite(newMax) ? undefined : newMax,
							})
						} else {
							return onChangeCommited(inputRange)
						}
					}}
					track={invertedRange ? 'inverted' : undefined}
					marks={marks}
					valueLabelDisplay='auto'
					valueLabelFormat={(value) => {
						if (infiniteOption && value === sliderMin) {
							return '-∞'
						} else if (infiniteOption && value === sliderMax) {
							return '∞'
						}

						return `${value} kA`
					}}
				/>
			</Box>
			<IconButton
				data-ta={ids.signalStrengthSlider.invertBtn}
				variant='text'
				icon='RotateReverse'
				label={t(($) => $.reports.lightning.form.signalStrengthFilter.invert)}
				size='small'
				color='primary'
				disabled={disabled}
				onClick={() => onChangeCommited({ ...inputRange, inverted: !inputRange.inverted })}
			/>
		</Box>
	)
}

export const SignalStrengthRangeSelectionSchema = z
	.object({ min: z.int().optional(), max: z.int().optional(), inverted: z.boolean() })
	.refine((range) => {
		if (range.min === undefined && range.max === undefined && range.inverted) {
			return false
		}

		return true
	})

export type SignalStrengthRangeSelection<TOptional extends boolean> = TOptional extends false
	? Required<z.infer<typeof SignalStrengthRangeSelectionSchema>>
	: z.infer<typeof SignalStrengthRangeSelectionSchema>
